// React imports
import React, { useCallback, useRef, useState } from "react";

// Shared sidebar width management. All sidebars (personal, workspace,
// walkthrough) read the same stored width so the layout feels consistent
// as the user moves between areas of the app.
const STORAGE_KEY = "bluebird-sidebar-width";
const MIN_WIDTH = 240;
const MAX_WIDTH = 560;
const DEFAULT_WIDTH = 300;
const KEYBOARD_STEP = 16;

const clampWidth = (width) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width));

export function useSidebarWidth() {
    const [width, setWidth] = useState(() => {
        try {
            const stored = parseInt(localStorage.getItem(STORAGE_KEY), 10);
            return Number.isNaN(stored) ? DEFAULT_WIDTH : clampWidth(stored);
        } catch {
            return DEFAULT_WIDTH;
        }
    });
    const [isResizing, setIsResizing] = useState(false);

    const applyWidth = useCallback((nextWidth) => {
        const next = clampWidth(Math.round(nextWidth));
        setWidth(next);
        try {
            localStorage.setItem(STORAGE_KEY, String(next));
        } catch {
            // Storage unavailable (private mode) — width still applies for the session.
        }
        return next;
    }, []);

    const resetWidth = useCallback(() => {
        setWidth(DEFAULT_WIDTH);
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            // Ignore storage errors.
        }
    }, []);

    return { width, isResizing, setIsResizing, applyWidth, resetWidth };
}

export function SidebarResizeHandle({ width, applyWidth, resetWidth, setIsResizing }) {
    const frameRef = useRef(null);

    const handlePointerDown = (e) => {
        // Only left button / primary touch starts a drag.
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = width;
        setIsResizing(true);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        const handlePointerMove = (moveEvent) => {
            if (frameRef.current) return;
            frameRef.current = requestAnimationFrame(() => {
                frameRef.current = null;
                applyWidth(startWidth + (moveEvent.clientX - startX));
            });
        };

        const handlePointerUp = () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
                frameRef.current = null;
            }
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            setIsResizing(false);
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowLeft") {
            e.preventDefault();
            applyWidth(width - KEYBOARD_STEP);
        } else if (e.key === "ArrowRight") {
            e.preventDefault();
            applyWidth(width + KEYBOARD_STEP);
        } else if (e.key === "Home" || e.key === "Enter") {
            e.preventDefault();
            resetWidth();
        }
    };

    return (
        <div
            className="Sidebar__resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            aria-valuenow={width}
            aria-valuemin={MIN_WIDTH}
            aria-valuemax={MAX_WIDTH}
            tabIndex={0}
            title="Drag to resize • Double-click to reset"
            onPointerDown={handlePointerDown}
            onDoubleClick={resetWidth}
            onKeyDown={handleKeyDown}
        />
    );
}
